import videoOverridesJson from './videoOverrides.json';

// Central registry of demo/tutorial videos shown on the website.
//
// Adding a new video = one entry here + i18n keys (videos.items.<id>.title /
// .description in every locale). No component changes needed.
//
// Videos are hosted on YouTube as UNLISTED (they never appear on the channel
// or in search; they only exist as embeds on our site, so language versions
// never mix in front of visitors). Thumbnails come from i.ytimg.com, embeds
// use youtube-nocookie.com with rel=0 + modestbranding=1.

export type VideoLanguage = 'sk' | 'cs' | 'da' | 'en' | 'de';
export type VideoCategoryId = 'overview' | 'howTo';
export type VideoStatus = 'published' | 'comingSoon' | 'hidden';

export interface DemoVideo {
  /** Registry id — also the i18n key under videos.items.<id> */
  id: string;
  /** Category id — i18n key under videos.categories.<category> */
  category: VideoCategoryId;
  /** published = playable once a YouTube ID exists; comingSoon = announced, not playable */
  status: VideoStatus;
  /**
   * YouTube video ID per language. A language without its own ID is not
   * playable there — no cross-language fallback (a Slovak recording must
   * never play on the Danish site just because the DA field is empty).
   */
  youtubeIds: Partial<Record<VideoLanguage, string>>;
  /** Canonical Slovak module slugs — embeds this video on those module landing pages */
  moduleSlugs?: string[];
  /** Duration in seconds (shown as m:ss and used for VideoObject structured data) */
  durationSec?: number;
  /** ISO date (YYYY-MM-DD) the video was published — used for VideoObject structured data */
  publishedAt?: string;
  /** false = hide from the hero dropdown (still shown in the video library). Default: shown */
  showInHero?: boolean;
  /** Homepage Top-3 slot: 1 = large feature card, 2–3 = smaller cards. Unset = not featured */
  homeSlot?: number;
}

/**
 * Dev-dashboard overrides (src/data/videoOverrides.json), edited via the
 * dev-only /admin/videos/ page. The file is committed with the code, so
 * dashboard changes reach production through a normal publish. Values here
 * shadow the code defaults below.
 */
export interface VideoTextOverride {
  title?: string;
  description?: string;
}

/** Shared UI labels of the video system (videos.* i18n keys, minus per-video items) */
export interface VideoUiOverride {
  cta?: string;
  menuTitle?: string;
  comingSoon?: string;
  languageNote?: string;
  moduleSectionHeading?: string;
  categories?: Partial<Record<VideoCategoryId, string>>;
}

export interface VideoOverride {
  status?: VideoStatus;
  youtubeIds?: Partial<Record<VideoLanguage, string>>;
  durationSec?: number;
  publishedAt?: string;
  /** Custom title/description per language — shadows videos.items.<id>.* i18n keys */
  texts?: Partial<Record<VideoLanguage, VideoTextOverride>>;
  /** Re-link to module landing pages (empty array clears all links) */
  moduleSlugs?: string[];
  /** @deprecated Legacy single-module link ('' cleared it) — still honored on read, new saves write moduleSlugs */
  moduleSlug?: string;
  /** Hide from the hero dropdown when false */
  showInHero?: boolean;
  /** Homepage Top-3 slot (1–3); must be unique across all videos */
  homeSlot?: number;
}
export type VideoOverrides = Record<string, VideoOverride>;
/** Dashboard-created video (reserved `custom` key) — no code entry exists */
export interface CustomVideoOverride extends VideoOverride {
  category: VideoCategoryId;
}
/** Full overrides file shape: video entries plus the reserved `ui`/`custom` keys */
export type VideoOverridesPayload = VideoOverrides & {
  ui?: Partial<Record<VideoLanguage, VideoUiOverride>>;
  custom?: Record<string, CustomVideoOverride>;
};

const videoOverridesFile = videoOverridesJson as Record<string, unknown>;
/** Raw overrides as authored via the dashboard (used by the admin page) */
export const rawVideoOverrides = videoOverridesFile as VideoOverridesPayload;
const videoOverrides = videoOverridesFile as VideoOverrides;
/** Reserved top-level key in the overrides file: shared UI label overrides */
const videoUiOverrides = (videoOverridesFile.ui ?? {}) as Partial<Record<VideoLanguage, VideoUiOverride>>;
/** Reserved top-level key: dashboard-created custom videos */
const customVideoEntries = (videoOverridesFile.custom ?? {}) as Record<string, CustomVideoOverride>;

// TODO(user): paste the YouTube ID of the existing Danish module-tour video
// here, or (easier) via the dev-only /admin/videos/ dashboard. Until filled,
// the video renders with a "coming soon" badge everywhere and cannot be played.
const DK_MODULE_TOUR_YOUTUBE_ID = '';

export const demoVideoCodeDefaults: DemoVideo[] = [
  {
    id: 'moduleTour',
    category: 'overview',
    status: 'published',
    youtubeIds: { da: DK_MODULE_TOUR_YOUTUBE_ID },
  },
  {
    id: 'createProject',
    category: 'howTo',
    status: 'comingSoon',
    moduleSlugs: ['projektove-riadenie'],
    youtubeIds: {},
  },
  {
    id: 'invoicingBasics',
    category: 'howTo',
    status: 'comingSoon',
    moduleSlugs: ['online-fakturacia'],
    youtubeIds: {},
  },
  {
    id: 'trackTime',
    category: 'howTo',
    status: 'comingSoon',
    moduleSlugs: ['vykaz-prace-a-evidencia-dochadzky'],
    youtubeIds: {},
  },
];

/**
 * Effective registry used across the whole site: code defaults merged with
 * the dev-dashboard overrides from videoOverrides.json, plus dashboard-created
 * custom videos appended at the end.
 */
/**
 * Normalize the module link(s) from an override. undefined = not overridden
 * (keep the code default), null = explicitly cleared, array = new links.
 * Legacy saves used the singular `moduleSlug` string ('' = cleared).
 */
export const overrideModuleSlugs = (override: VideoOverride): string[] | null | undefined => {
  if (override.moduleSlugs !== undefined) {
    const unique = [...new Set(override.moduleSlugs)];
    return unique.length ? unique : null;
  }
  if (override.moduleSlug !== undefined) return override.moduleSlug ? [override.moduleSlug] : null;
  return undefined;
};

export const demoVideos: DemoVideo[] = [
  ...demoVideoCodeDefaults.map((video) => {
    const override = videoOverrides[video.id];
    if (!override) return video;
    const moduleSlugs = overrideModuleSlugs(override);
    return {
      ...video,
      status: override.status ?? video.status,
      youtubeIds: { ...video.youtubeIds, ...override.youtubeIds },
      durationSec: override.durationSec ?? video.durationSec,
      publishedAt: override.publishedAt ?? video.publishedAt,
      moduleSlugs: moduleSlugs === undefined ? video.moduleSlugs : moduleSlugs ?? undefined,
      showInHero: override.showInHero ?? video.showInHero,
      homeSlot: override.homeSlot ?? video.homeSlot,
    };
  }),
  ...Object.entries(customVideoEntries).map(([id, custom]) => ({
    id,
    category: custom.category ?? 'howTo',
    status: custom.status ?? 'comingSoon',
    youtubeIds: custom.youtubeIds ?? {},
    moduleSlugs: overrideModuleSlugs(custom) ?? undefined,
    durationSec: custom.durationSec,
    publishedAt: custom.publishedAt,
    showInHero: custom.showInHero,
    homeSlot: custom.homeSlot,
  })),
];

/** Display order of category sections on the video library page */
export const videoCategories: VideoCategoryId[] = ['overview', 'howTo'];

/**
 * Apply text overrides (video titles/descriptions + shared UI labels) onto an
 * i18n `common` bundle in place. Called once when the i18n config module
 * loads, so every init path (client, SSR restore, server) serves the
 * customized texts. The overrides file ships with the code, so dashboard
 * changes reach production through a normal publish.
 */
export function applyVideoTextOverridesToCommon(common: unknown, lang: VideoLanguage): void {
  if (!common || typeof common !== 'object') return;
  const videosNs = (common as Record<string, any>).videos;
  if (!videosNs) return;

  for (const [videoId, override] of Object.entries(videoOverrides)) {
    if (videoId === 'ui' || videoId === 'custom') continue;
    const texts = (override as VideoOverride).texts?.[lang];
    const item = videosNs.items?.[videoId];
    if (!texts || !item) continue;
    if (texts.title) item.title = texts.title;
    if (texts.description) item.description = texts.description;
  }

  // Custom (dashboard-created) videos have no locale entries — inject only
  // the copy authored for this language. Never fall back to Slovak or the
  // title will leak onto .dk / .cz / .com (see isVideoListed).
  for (const [videoId, custom] of Object.entries(customVideoEntries)) {
    const title = custom.texts?.[lang]?.title?.trim();
    if (!title) continue;
    if (!videosNs.items) videosNs.items = {};
    videosNs.items[videoId] = {
      title,
      description: custom.texts?.[lang]?.description ?? '',
    };
  }

  const ui = videoUiOverrides[lang];
  if (!ui) return;
  if (ui.cta) videosNs.cta = ui.cta;
  if (ui.menuTitle) videosNs.menuTitle = ui.menuTitle;
  if (ui.comingSoon) videosNs.comingSoon = ui.comingSoon;
  if (ui.languageNote) videosNs.languageNote = ui.languageNote;
  if (ui.moduleSectionHeading && videosNs.moduleSection) {
    videosNs.moduleSection.heading = ui.moduleSectionHeading;
  }
  if (ui.categories && videosNs.categories) {
    for (const categoryId of videoCategories) {
      const label = ui.categories[categoryId];
      if (label) videosNs.categories[categoryId] = label;
    }
  }
}

/**
 * YouTube ID for this language only. Empty / missing IDs do not fall back
 * to another locale — otherwise Danish visitors would get the Slovak cut.
 */
export function resolveYouTubeId(video: DemoVideo, lang: VideoLanguage): string | undefined {
  const id = video.youtubeIds[lang]?.trim();
  return id || undefined;
}

/** True when the video is shown in a language that has no own recording yet */
export function isFallbackLanguage(video: DemoVideo, lang: VideoLanguage): boolean {
  return !!resolveYouTubeId(video, lang) && !video.youtubeIds[lang]?.trim();
}

/** A video is playable only when published AND this locale has its own YouTube ID */
export function isVideoPlayable(video: DemoVideo, lang: VideoLanguage): boolean {
  return video.status === 'published' && !!resolveYouTubeId(video, lang);
}

/** Hidden videos never render on the site (hero dropdown, library, module sections/cards) */
export function isVideoVisible(video: DemoVideo): boolean {
  return video.status !== 'hidden';
}

/** Dashboard-created video with a title written for this locale (no SK fallback). */
function hasAuthoredLocaleTitle(videoId: string, lang: VideoLanguage): boolean {
  const custom = customVideoEntries[videoId];
  if (custom) return !!custom.texts?.[lang]?.title?.trim();
  const override = videoOverrides[videoId] as VideoOverride | undefined;
  return !!override?.texts?.[lang]?.title?.trim();
}

/**
 * Public listing filter: playable in this locale, or announced as coming
 * soon *in this locale*. Published videos without a locale YouTube ID stay
 * off the site. Custom coming-soon cards without a locale title stay off
 * too — otherwise the Slovak name appears on /videoer/ as „Kommer snart".
 */
export function isVideoListed(video: DemoVideo, lang: VideoLanguage): boolean {
  if (!isVideoVisible(video)) return false;
  if (isVideoPlayable(video, lang)) return true;
  if (video.status !== 'comingSoon') return false;
  // Custom cards have no i18n keys — only list when this locale has a title.
  if (customVideoEntries[video.id]) return hasAuthoredLocaleTitle(video.id, lang);
  return true;
}

/** All videos linked to a module landing page (canonical Slovak slug) */
export function getVideosForModule(moduleSlug: string): DemoVideo[] {
  return demoVideos.filter((video) => video.moduleSlugs?.includes(moduleSlug));
}

/** Standard YouTube thumbnail (hqdefault always exists, unlike maxresdefault) */
export function getYouTubeThumbnail(youtubeId: string | undefined): string | undefined {
  return youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : undefined;
}

/** Privacy-enhanced embed URL (youtube-nocookie, no unrelated recommendations) */
export function getYouTubeEmbedUrl(youtubeId: string, autoplay = true): string {
  const params = new URLSearchParams({ rel: '0', modestbranding: '1' });
  if (autoplay) params.set('autoplay', '1');
  return `https://www.youtube-nocookie.com/embed/${youtubeId}?${params.toString()}`;
}

/** "m:ss" for the duration badge */
export function formatDuration(durationSec?: number): string | undefined {
  if (!durationSec || durationSec <= 0) return undefined;
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** ISO 8601 duration (PT#M#S) for VideoObject structured data */
export function formatDurationIso(durationSec?: number): string | undefined {
  if (!durationSec || durationSec <= 0) return undefined;
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  return `PT${minutes}M${seconds}S`;
}
